/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { Else, If, Then } from "react-if";
import { Text, Spacer, Tag } from "@nordcloud/gnui";
import { CollapseItem } from "~/components/CollapseItem/CollapseItem";
import { DetailsTitle } from "~/components/Styled/Common";
import FlexContainer from "~/components/Styled/FlexContainer";
import { Sla, SLAUnitType } from "~/models";
import { displaySlaType, displaySlaUnitType } from "~/tools";
import { displayServiceHours } from "~/tools/";
import { InlineUnusedSLAEditor } from "./components/InlineUnusedSLAEditor";
import { useSLANameModification } from "./hooks";

type Props = {
  sla: Sla;
  isNarrow?: boolean;
  refetch?: (id?: string) => Promise<void>;
  serviceId?: string;
};

export function SLAItem({ sla, isNarrow, refetch, serviceId }: Props) {
  const { isNameEditorEnabled } = useSLANameModification(sla.id, refetch);

  return (
    <CollapseItem
      isNarrow
      name={
        <If condition={isNameEditorEnabled}>
          <Then>
            <div css={{ display: "flex" }}>
              <Text
                size="md"
                weight="medium"
                tag="div"
                css={{ margin: "0 1rem 0 0" }}
              >
                {sla?.name}
              </Text>
              <InlineUnusedSLAEditor
                refetch={refetch as () => void}
                sla={sla}
                serviceId={serviceId ?? ""}
              />
            </div>
          </Then>
          <Else>
            <Text size="md" weight="medium" tag="div">
              {sla?.name}
            </Text>
          </Else>
        </If>
      }
    >
      <Row>
        <Col>
          <Spacer height="2.5rem" />
          <Row>
            <Col
              xs={12}
              sm={isNarrow ? 12 : 5}
              md={isNarrow ? 8 : 4}
              lg={isNarrow ? 8 : 5}
            >
              <FlexContainer
                direction="column"
                align="flex-start"
                justify="flex-start"
                grow
              >
                <FlexContainer align="center">
                  <DetailsTitle isNarrow={isNarrow}>SLA Type</DetailsTitle>
                  {displaySlaType(sla?.slaType)}
                </FlexContainer>
                <Spacer height="1rem" />
                <FlexContainer align="center">
                  <DetailsTitle isNarrow={isNarrow}>
                    Support Service {isNarrow && <br />} Hours:
                  </DetailsTitle>
                  {displayServiceHours(sla?.supportHours)}
                </FlexContainer>
                <Spacer height="1rem" />
                <FlexContainer align="center">
                  <DetailsTitle isNarrow={isNarrow}>Info:</DetailsTitle>
                  {sla?.description || "-"}
                </FlexContainer>
              </FlexContainer>
            </Col>
            <Col
              xs={12}
              sm={isNarrow ? 12 : 4}
              md={isNarrow ? 8 : 4}
              lg={isNarrow ? 8 : 5}
            >
              <FlexContainer
                direction="column"
                align="flex-start"
                justify="flex-start"
                grow
              >
                {isNarrow && <Spacer height="1rem" />}
                <FlexContainer align="center">
                  <DetailsTitle isNarrow={isNarrow}>SLA Unit Type</DetailsTitle>
                  {displaySlaUnitType(sla?.unitType)}
                </FlexContainer>
                {sla?.unitType === SLAUnitType.VALUE_RANGE && (
                  <Spacer height="1rem" />
                )}
                <FlexContainer align="center">
                  {sla?.unitType === SLAUnitType.VALUE_RANGE && (
                    <>
                      <DetailsTitle isNarrow={isNarrow}>
                        <FlexContainer direction="column" align="flex-start">
                          Value Range
                          {isNarrow && <Tag text={sla?.range?.unit} />}
                        </FlexContainer>
                      </DetailsTitle>
                      <Text css={{ marginRight: "1rem" }} tag="div">
                        Min: {sla?.range?.min}
                      </Text>
                      <Text css={{ marginRight: "1rem" }} tag="div">
                        Max: {sla?.range?.max}
                      </Text>
                      <Text tag="div">Default: {sla?.range?.default}</Text>
                      {!isNarrow && (
                        <Tag
                          text={sla?.range?.unit}
                          css={{ margin: "0 1rem" }}
                        />
                      )}
                    </>
                  )}
                </FlexContainer>
                <Spacer height="1rem" />
                <FlexContainer align="center">
                  <DetailsTitle isNarrow={isNarrow}>
                    Priority value:
                  </DetailsTitle>
                  {sla?.priority || "-"}
                </FlexContainer>
              </FlexContainer>
            </Col>
          </Row>
        </Col>
      </Row>
    </CollapseItem>
  );
}
